import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Calendar, RefreshCw, Building2 } from 'lucide-react';
import { apiService, DepartmentRevenue, ExecutiveDashboardData } from '../services/api';

const DEPT_COLORS: Record<string, string> = {
  'Club Sales (Callaway)': '#2563eb',
  'Front-End Sales': '#7c3aed',
  'Membership Sales': '#059669',
  'Product Sales': '#d97706',
  'School Sales': '#dc2626',
  'Trade-In Sales': '#0891b2',
  'Uncategorized': '#6b7280',
};

interface DateRange {
  startDate: string;
  endDate: string;
}

const Analytics: React.FC = () => {
  const [execData, setExecData] = useState<ExecutiveDashboardData | null>(null);
  const [deptRevenue, setDeptRevenue] = useState<DepartmentRevenue[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });
  const [customDateRange, setCustomDateRange] = useState<DateRange>({ ...dateRange });
  const [showCustom, setShowCustom] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setIsRefreshing(true);
      setLoading(true);
      await Promise.all([apiService.loadPlatforms(), apiService.loadDepartments()]);

      const execResult = await apiService.getExecutiveDashboard('custom', dateRange.startDate, dateRange.endDate);
      setExecData(execResult);
      // Use department data from the same RPC for consistency
      if (execResult?.departments) {
        setDeptRevenue(execResult.departments.map(d => ({
          department_name: d.name,
          department_id: d.departmentId,
          successful_payments: d.txCount,
          refund_count: 0,
          total_revenue: d.grossRevenue,
          total_refunds: d.totalRefunds,
          net_revenue: d.netRevenue,
        })));
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  useEffect(() => { loadData(); }, [loadData]);

  const setQuickRange = (days: number) => {
    const newRange = {
      startDate: new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
    };
    setDateRange(newRange);
    setCustomDateRange(newRange);
    setShowCustom(false);
  };

  const applyCustomRange = () => {
    setDateRange(customDateRange);
    setShowCustom(false);
  };

  const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
  const fmtFull = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);
  const pct = (num: number, denom: number) =>
    denom === 0 ? '0%' : `${((num / denom) * 100).toFixed(1)}%`;

  const formatDateLabel = () => {
    const s = new Date(dateRange.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const e = new Date(dateRange.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${s} — ${e}`;
  };

  const isActiveRange = (days: number) => {
    const expected = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    return dateRange.startDate === expected;
  };

  if (loading && !execData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading analytics...</span>
      </div>
    );
  }

  if (!execData) {
    return (
      <div className="text-center py-16 text-gray-500">Unable to load analytics data.</div>
    );
  }

  const totalGross = deptRevenue.reduce((s, d) => s + Number(d.total_revenue), 0);
  const totalRefunds = deptRevenue.reduce((s, d) => s + Number(d.total_refunds), 0);
  const totalNet = deptRevenue.reduce((s, d) => s + Number(d.net_revenue), 0);
  const startMs = new Date(dateRange.startDate).getTime();
  const endMs = new Date(dateRange.endDate).getTime();
  const periodDays = Math.max(Math.round((endMs - startMs) / (24 * 60 * 60 * 1000)), 1);
  const kpis = execData.kpis;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-sm text-gray-500">Revenue performance by department and platform</p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-400">
          <Calendar className="h-4 w-4" />
          <span>{formatDateLabel()}</span>
        </div>
      </div>

      {/* Date Controls */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-2">
          {[
            { label: '7D', days: 7 },
            { label: '30D', days: 30 },
            { label: '90D', days: 90 },
            { label: '6M', days: 180 },
            { label: '1Y', days: 365 },
            { label: 'All', days: 730 },
          ].map(({ label, days }) => (
            <button
              key={label}
              onClick={() => setQuickRange(days)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                isActiveRange(days) ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
          <button
            onClick={() => setShowCustom(!showCustom)}
            className={`px-3 py-1.5 text-sm rounded-md ${showCustom ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Custom
          </button>
          <button
            onClick={loadData}
            disabled={isRefreshing}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 ml-auto"
          >
            <RefreshCw className={`h-3.5 w-3.5 inline mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        {showCustom && (
          <div className="mt-3 flex items-end gap-3 pt-3 border-t border-gray-100">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Start</label>
              <input type="date" value={customDateRange.startDate}
                onChange={(e) => setCustomDateRange(p => ({ ...p, startDate: e.target.value }))}
                className="px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">End</label>
              <input type="date" value={customDateRange.endDate}
                onChange={(e) => setCustomDateRange(p => ({ ...p, endDate: e.target.value }))}
                className="px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <button onClick={applyCustomRange} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700">
              Apply
            </button>
          </div>
        )}
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Gross Revenue', value: fmt(kpis.grossRevenue.current), sub: `${fmt(kpis.grossRevenue.current / periodDays)}/day` },
          { label: 'Net Revenue', value: fmt(kpis.netRevenue.current), sub: `${fmt(kpis.netRevenue.current / periodDays)}/day` },
          { label: 'Transactions', value: kpis.txCount.current.toLocaleString(), sub: `${(kpis.txCount.current / periodDays).toFixed(0)}/day` },
          { label: 'Avg Order Value', value: fmtFull(kpis.avgOrder.current), sub: `${kpis.txCount.current.toLocaleString()} successful` },
          { label: 'Refund Rate', value: `${kpis.refundRate.current.toFixed(1)}%`, sub: fmt(kpis.totalRefunds.current) },
        ].map(({ label, value, sub }) => (
          <div key={label} className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">{label}</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
          </div>
        ))}
      </div>

      {/* Department Revenue Bars */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center space-x-2">
            <Building2 className="h-5 w-5 text-gray-500" />
            <h2 className="text-base font-semibold text-gray-900">Department Revenue</h2>
          </div>
          <div className="text-sm text-gray-400">{formatDateLabel()}</div>
        </div>

        {deptRevenue.length === 0 ? (
          <p className="text-center text-gray-400 py-8">No data for this period</p>
        ) : (
          <div className="space-y-4">
            {deptRevenue.sort((a, b) => Number(b.net_revenue) - Number(a.net_revenue)).map((dept) => {
              const gross = Number(dept.total_revenue);
              const refunds = Number(dept.total_refunds);
              const net = Number(dept.net_revenue);
              const maxNet = Math.max(...deptRevenue.map(d => Number(d.net_revenue)), 1);
              const barWidth = (net / maxNet) * 100;
              const color = DEPT_COLORS[dept.department_name] || '#6b7280';
              return (
                <div key={dept.department_name}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center space-x-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                      <span className="text-sm font-medium text-gray-900">{dept.department_name}</span>
                    </div>
                    <div className="flex items-center space-x-4 text-sm">
                      <span className="text-gray-500">{Number(dept.successful_payments).toLocaleString()} payments</span>
                      {refunds > 0 && <span className="text-red-500">-{fmtFull(refunds)} refunds ({pct(refunds, gross)})</span>}
                      <span className="font-semibold text-gray-900">{fmtFull(net)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                      <div
                        className="h-3 rounded-full transition-all duration-500"
                        style={{ width: `${Math.max(barWidth, 1)}%`, backgroundColor: color }}
                      />
                    </div>
                    <span className="text-xs text-gray-400 w-12 text-right">{pct(net, totalNet)}</span>
                  </div>
                </div>
              );
            })}

            {/* Totals row */}
            <div className="pt-3 mt-2 border-t-2 border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-900">Total</span>
                <div className="flex items-center space-x-4 text-sm">
                  <span className="text-gray-500">{deptRevenue.reduce((s, d) => s + Number(d.successful_payments), 0).toLocaleString()} payments</span>
                  <span className="text-red-500">-{fmtFull(totalRefunds)} refunds ({pct(totalRefunds, totalGross)})</span>
                  <span className="font-bold text-gray-900">{fmtFull(totalNet)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Period Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Period', value: `${periodDays} days`, bg: 'bg-blue-50', text: 'text-blue-900' },
          { label: 'Daily Net Revenue', value: fmt(kpis.netRevenue.current / periodDays), bg: 'bg-green-50', text: 'text-green-900' },
          { label: 'Avg Transaction', value: fmtFull(kpis.avgOrder.current), bg: 'bg-purple-50', text: 'text-purple-900' },
          { label: 'Refund Total', value: fmt(kpis.totalRefunds.current), bg: 'bg-orange-50', text: 'text-orange-900' },
        ].map(({ label, value, bg, text }) => (
          <div key={label} className={`${bg} rounded-lg p-4 text-center`}>
            <p className={`text-xs font-medium ${text} uppercase opacity-70`}>{label}</p>
            <p className={`text-xl font-bold ${text} mt-1`}>{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Analytics;
